import { useEffect, useMemo, useState, type ComponentType, type ReactNode } from "react";
import { ProjectConfig, resolveEndpoints, type TimelineDocument, type TourDocument } from "@heroncendre/schema";
import {
  CubeGridBackdrop,
  FormatNotice,
  StartScreen,
  TileMemoryReadout,
  TourPlayer,
  useTourDocument,
  useTourState,
} from "@heroncendre/player";
import projectJson from "../project.json";
import { estParcours } from "./choix.js";
import { Bandeau } from "./Bandeau.js";
import { DemoPanel } from "./DemoPanel.js";
import { FonduEntree } from "./FonduEntree.js";
import { FlechesRotation } from "./FlechesRotation.js";
import { IndicateurRotation } from "./IndicateurRotation.js";
import { chaines, parcoursDemande, premierParcours } from "./parcours.js";
import { GuideInterface } from "./guide/GuideInterface.js";
import { ParcoursGuide } from "./ParcoursGuide.js";
import { SourceBadge } from "./SourceBadge.js";
import { BoutonAccueil } from "./BoutonAccueil.js";
import { SceneNav } from "./SceneNav.js";
import { SceneTitle } from "./SceneTitle.js";
import { etatNavigation } from "./etatNavigation.js";
import { EnteteInfos } from "./info/EnteteInfos.js";
import { InfoMenu } from "./info/InfoMenu.js";
import { SplashScreen } from "./SplashScreen.js";
import { VariantSwitcher } from "./VariantSwitcher.js";
import { NomsHotspots } from "./NomsHotspots.js";
import { PulseHotspots } from "./PulseHotspots.js";
import { RaccourciEdition } from "./RaccourciEdition.js";
import { sansScenesFermees, scenesFermees } from "./scenesReservees.js";
import { PARAM_SCENE, UrlScene } from "./UrlScene.js";
import { centeredStyle } from "./styles.js";

/**
 * ============================================================================
 *  Château de Saint-Sixt — la coquille
 * ----------------------------------------------------------------------------
 *  Tout le code de ce projet tient ici et dans deux modules voisins. Le reste
 *  est de la donnée : un document servi depuis R2, des tuiles adressées par
 *  contenu, un registre qui relie un commit à l'état des médias en ligne.
 *
 *  Le moteur est une dépendance ÉPINGLÉE (`0.10.2`, jamais `^0.10`). Cette
 *  visite ne changera pas parce que le moteur a évolué pour un autre client ;
 *  sa montée de version sera un acte délibéré.
 *
 *  ── L'habillage n'est pas encore écrit ─────────────────────────────────────
 *
 *  Ce qui suit est volontairement NEUTRE : les briques du moteur, posées aux
 *  quatre coins, sans ornement. Chaque endroit où l'identité du château
 *  viendra se poser est signalé par un commentaire « POINT D'EXTENSION ». Le
 *  modèle de ce que cela donne une fois habillé est `tour-au-14` (annonce de
 *  scène composée, sélecteur de langue dessiné, filets).
 * ==========================================================================*/

const project = ProjectConfig.parse(projectJson);
const params = new URLSearchParams(window.location.search);

const EDIT_MODE = params.has("edit");

/**
 * `?live` — consulter la visite publiée depuis un poste de développement.
 * Sans ce paramètre, `pnpm dev` lit le document et les assets locaux.
 */
const LIVE_MODE = params.has("live");

/**
 * `?demo=wedding` — raccourci de travail : la visite jouée directement en parcours
 * narratif, avec l'écran « Démarrer » du moteur, sans passer par l'accueil.
 */
const TIMELINE_DEMO = parcoursDemande(params.get("demo"));

/**
 * `?debug=true` — montre le témoin de provenance (local / publié, et le compte
 * des octets par source). Le témoin ne s'affiche plus d'office en développement :
 * il occupait le haut de l'écran sur chaque capture. Valeur EXACTE `true`, pour
 * qu'un `?debug` oublié dans une adresse partagée ne l'active pas.
 */
const DEBUG_BADGE = params.get("debug") === "true";

/**
 * Les scènes réservées que l'adresse n'ouvre pas (`?pratique`… voir
 * `scenesReservees.ts`) : retirées du document de la VISITE, jamais de l'éditeur.
 */
const SCENES_FERMEES = scenesFermees(params);


/** `?libre` — raccourci de travail : la visite libre, sans passer par l'accueil. */
const LIBRE_DIRECT = params.has("libre");

/**
 * `?scene=<id>` — l'adresse d'une scène de la visite libre (voir `UrlScene.tsx`).
 * Un lien partagé amène directement sur la scène, sans passer par l'accueil.
 * Non validé ici : le document n'est pas encore chargé ; un identifiant inconnu
 * retombe sur la scène d'entrée.
 */
const SCENE_URL = params.get(PARAM_SCENE);

/**
 * Où en est le visiteur. Au lancement, l'ACCUEIL propose la visite libre ou un
 * parcours narratif (`accueil.json`) ; les raccourcis ci-dessus le sautent.
 */
type Choix =
  | { mode: "accueil" }
  | { mode: "libre" }
  /** `guideId` : l'identifiant de la carte d'accueil (`mariage`), clé de la progression mémorisée. */
  | { mode: "parcours"; timeline: TimelineDocument; guideId: string }
  | { mode: "demo" };

function choixInitial(): Choix {
  if (TIMELINE_DEMO) return { mode: "demo" };
  return LIBRE_DIRECT || SCENE_URL ? { mode: "libre" } : { mode: "accueil" };
}

const endpoints = resolveEndpoints(project, import.meta.env.DEV && !LIVE_MODE);

type EditorComponent = ComponentType<{ tour: TourDocument; assetBaseUrl: string }>;

/**
 * L'éditeur est chargé À LA DEMANDE.
 *
 * Un import statique le ferait entrer dans le bundle principal, et chaque
 * visiteur téléchargerait des panneaux qu'il n'ouvrira jamais. Un import
 * dynamique en fait un fragment distinct, que seul `?edit` va chercher — c'est
 * ce qui rend vraie DANS LE BUNDLE la promesse « la visite livrée n'embarque
 * rien de l'éditeur », et pas seulement dans l'intention.
 *
 * Écrit à la main plutôt qu'avec `React.lazy` : c'est le même mécanisme d'état
 * asynchrone que partout ailleurs, sans imposer un `Suspense` pour un unique
 * point de chargement.
 */
function useEditorView(enabled: boolean): EditorComponent | null {
  const [view, setView] = useState<EditorComponent | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    void import("./EditorView.js").then((module) => {
      // La fonction de mise à jour reçoit un COMPOSANT : sans l'enrober, React
      // l'appellerait comme un calculateur d'état.
      if (!cancelled) setView(() => module.EditorView);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return view;
}

export function App() {
  const state = useTourDocument(endpoints.documentUrl, project.tenantId);
  const EditorView = useEditorView(EDIT_MODE);
  const [choix, setChoix] = useState<Choix>(choixInitial);
  // Le noir qui enchaîne l’accueil et la première scène d’un parcours guidé (`FonduEntree`).
  const [fonduEntree, setFonduEntree] = useState(false);
  // Le visiteur a déjà vu une partie du parcours et choisit : reprendre ou recommencer (`ChoixReprise`).
  const [questionReprise, setQuestionReprise] = useState(false);
  // Le logo et le bouton « Accueil » : même geste (`useRetourAccueil`), même écran d'arrivée.
  const retourAccueil = () => {
    etatNavigation.setOuvert(false);
    setFonduEntree(false);
    setChoix({ mode: "accueil" });
  };
  // « Quitter le guide » ramène au choix du type de visite ; « Revenir à la visite libre » y va directement.
  const sortieGuide = (vers: "accueil" | "libre") => (vers === "accueil" ? retourAccueil() : setChoix({ mode: "libre" }));
  const debug = useDebug();
  const visite = useMemo(
    () => (state.status === "ready" ? sansScenesFermees(state.tour, SCENES_FERMEES) : undefined),
    [state],
  );

  if (state.status === "loading") {
    return <div style={centeredStyle}>Chargement de la visite…</div>;
  }

  if (state.status === "error") {
    return (
      <div style={centeredStyle}>
        <strong style={{ color: "#f88" }}>{state.message}</strong>
        <pre style={{ margin: 0, fontSize: 12, opacity: 0.75, whiteSpace: "pre-wrap" }}>
          {state.issues.join("\n")}
        </pre>
      </div>
    );
  }

  const { tour: documentComplet, issues } = state;

  if (EDIT_MODE) {
    if (!EditorView) return <div style={centeredStyle}>Chargement de l'éditeur…</div>;
    return <EditorView tour={documentComplet} assetBaseUrl={endpoints.assetBaseUrl} />;
  }

  // La visite, elle, ne reçoit que les scènes que l'adresse ouvre.
  const tour = visite ?? documentComplet;

  // Le player reçoit toujours un parcours à l'accueil : son lecteur de voix off doit
  // exister pour être amorcé dans le geste du clic (voir `Accueil.tsx`). En visite
  // libre, il n'en reçoit plus : rien du mode guidé n'est monté.
  const timeline: TimelineDocument | undefined =
    choix.mode === "demo"
      ? TIMELINE_DEMO
      : choix.mode === "parcours"
        ? choix.timeline
        : choix.mode === "accueil"
          ? premierParcours()
          : undefined;

  // La scène demandée par l'adresse, si le document la connaît.
  const startSceneId = tour.scenes.some((scene) => scene.id === SCENE_URL) ? SCENE_URL! : undefined;

  return (
    <TourPlayer
      tour={tour}
      assetBaseUrl={endpoints.assetBaseUrl}
      startSceneId={startSceneId}
      /* Les six faces du cube, quadrillées, là où la scène n'a pas encore de
         panorama — et nulle part ailleurs : le décor se règle sur l'état de la
         source et s'efface de lui-même dès qu'elle est tuilée. Rien à retirer
         d'ici le jour venu.

         C'est ce qui rend les scènes d'exemple utilisables tout de suite : on
         peut y poser des hotspots et régler les vues dans l'éditeur avant
         d'avoir la moindre image. */
      backdrop={<CubeGridBackdrop />}
      timeline={timeline}
    >
      {/* POINT D'EXTENSION — l'accueil. Un écran à part entière, au-dessus du
          panorama ET du reste de la coquille (`zIndex`) : pendant qu'il est là,
          le sélecteur de langue du moteur et le bandeau sont démontés plutôt que
          masqués, pour ne pas rester atteignables au clavier derrière lui. */}
      {choix.mode === "accueil" && (
        <SplashScreen
          tour={tour}
          assetBaseUrl={endpoints.assetBaseUrl}
          onSelect={(item) => {
            if (!estParcours(item)) return setChoix({ mode: "libre" });
            const parcours = parcoursDemande(item.parcours);
            // Un parcours qui n'existe pas encore : l'item est désactivé, rien ne part.
            if (parcours) {
              setFonduEntree(true);
              setChoix({ mode: "parcours", timeline: parcours, guideId: item.id });
            }
          }}
        />
      )}

      {/* Largeur du plus long titre VISIBLE, recalculée par le navigateur à
          chaque dépliage — pas de mesure en JavaScript, donc rien à rater quand
          la page s'ouvre groupes repliés. À 200 px fixes, un groupe déplié
          débordait (23 titres coupés, barre horizontale). Plafonnée à 420 px —
          les titres du pigeonnier en demandent 404 —, et à la largeur de
          l'écran moins les marges sur un téléphone.
          `scrollbarGutter` réserve la place de la barre verticale : sans lui,
          son apparition mangerait la largeur calculée et ferait revenir la
          barre horizontale, que `overflowX` interdit de toute façon. */}
      {/* En parcours guidé, le menu, le plan et les variantes s'effacent : ils
          se disputeraient le haut et le bas de l'écran avec le fil d'Ariane, les
          sous-titres et les contrôles, et le parcours est justement ce qui
          remplace la navigation libre. */}
      {/* POINT D'EXTENSION — l'en-tête de la visite, au logo du château.

          Posé AVANT le menu et les langues : son dégradé passe sous eux, il ne
          les assombrit pas. Il ne capte aucun geste — on tourne la vue à
          travers —, sauf le logo, qui ramène à la première scène. En parcours,
          pendant un parcours, la bande du guide prend le haut : l'en-tête s'efface. */}
      {choix.mode !== "accueil" && <Bandeau tour={tour} assetBaseUrl={endpoints.assetBaseUrl} onAccueil={retourAccueil} />}

      <UrlScene actif={choix.mode === "libre"} />
      {/* La touche E : à l'accueil, il n'y a pas encore de scène à éditer. */}
      <RaccourciEdition actif={choix.mode !== "accueil"} />
      {/* ← → tournent la vue en visite libre ; en parcours, elles sont à la voix off. */}
      <FlechesRotation actif={choix.mode === "libre"} />

      <Libre mode={choix.mode}>
      {/* POINT D'EXTENSION — la navigation entre scènes, « carnet » : étiquette
          « où suis-je » centrée à gauche, panneau Plan / Liste (feuille basse sur
          téléphone). Les espaces, plans et points sont ceux du document. */}
      <SceneNav assetBaseUrl={endpoints.assetBaseUrl} />
      {/* Le moteur ne monte aucun hotspot tant qu'il tient une timeline (accueil, parcours) :
          ni halo ni nom sans eux. Sans timeline — la visite libre — ils sont là. */}
      {choix.mode === "libre" && (
        <>
          {/* Un souffle de lumière sur les points cliquables : le moteur ne les anime qu'au survol. */}
          <PulseHotspots />
          {/* Sur téléphone, pas de survol : le nom de la destination est affiché sur chaque point. */}
          <NomsHotspots />
        </>
      )}
      </Libre>

      {/* L'en-tête : le retour à l'accueil en haut à gauche, les langues seules en
          haut à droite, le logo au centre (`Bandeau`).

          POINT D'EXTENSION — `SelecteurLangue` est celui de l'accueil, la
          référence de design, repris tel quel : même dessin partout. */}
      {(choix.mode === "libre" || choix.mode === "parcours") && (
        <HorsParcours>
          <div style={{ position: "absolute", top: 12, left: 12 }}>
            <BoutonAccueil onAccueil={retourAccueil} />
          </div>
        </HorsParcours>
      )}
      {choix.mode !== "accueil" && (
        <HorsParcours>
          <EnteteInfos />
        </HorsParcours>
      )}

      {/* POINT D'EXTENSION — l'annonce de scène : le titre d'arrivée « signature »
          (handoff `design_handoff_titre_scene`), en bas à gauche, cinq secondes
          dès que la scène est prête.

          En visite libre, et seulement là : ni sous l'accueil (il se superposerait
          au choix de la visite), ni pendant un parcours narratif, où la voix off
          dit déjà où l'on est. Sur ordinateur, la navigation s'efface le temps du
          titre et revient telle qu'elle était (`etatNavigation`). */}
      <Libre mode={choix.mode}>
        <SceneTitle />

        {/* POINT D'EXTENSION — le contrôleur d'ambiances (jour, coucher, soir,
            nuit…), au bas de l'écran, sur les scènes qui ont plusieurs versions.
            En visite libre, et quand on quitte un parcours pour explorer : pendant
            la voix, ce sont les moments des plans qui conduisent la lumière. */}
        <VariantSwitcher assetBaseUrl={endpoints.assetBaseUrl} />
      </Libre>
      {/* POINT D'EXTENSION — le menu d'informations et des crédits (handoff `design_handoff_credits`).
          Il remplace le menu du clic droit du moteur (« Bienvenue… Made in Annecy with ♥ »), qui n'est
          plus monté : le visiteur y accède par le bouton ⓘ / ⋯, dans la visite libre comme en mode
          main du guide. Les boutons sont posés où le design les veut (`EnteteInfos`, `SceneNav`, bandes
          du guide) ; ses surcouches, elles, sont montées ici, une fois, au-dessus de tout. */}
      {choix.mode !== "accueil" && <InfoMenu assetBaseUrl={endpoints.assetBaseUrl} surAccueil={retourAccueil} />}

      {/* Le noir de l’accueil, tenu jusqu’à l’apparition de la première scène du guide. */}
      {fonduEntree && <FonduEntree surFin={() => setFonduEntree(false)} suspendu={questionReprise} />}
      <FormatNotice style={{ position: "absolute", bottom: 60, right: 12 }} />

      {/* Le parcours narratif : l'interface du mode guidé (`guide/GuideInterface`, thème
          Écrin) — bandes noires, barre de la main, fin de chapitre.

          Choisi à l'accueil, il démarre tout de suite (`ParcoursGuide`). Par le
          raccourci `?demo`, il passe par l'écran « Démarrer » du moteur et affiche
          les compteurs de la démo. */}
      {choix.mode === "parcours" && (
        <ParcoursGuide timeline={choix.timeline} guideId={choix.guideId} surSortie={sortieGuide} surAttente={setQuestionReprise} />
      )}
      {choix.mode === "demo" && (
        <>
          {/* L'écran « Démarrer » du moteur donne le geste qui débloque l'audio ; l'interface suit. */}
          <StartScreen strings={chaines} />
          <GuideInterface surSortie={sortieGuide} />
          <DemoPanel />
        </>
      )}

      {/* POINT D'EXTENSION — « Regardez autour de vous » : après l'accueil en visite
          libre, après cinq secondes de voix au premier chapitre d'un parcours. Une
          seule fois par chargement (mémoire durable coupée, voir `MEMORISER`). Posé après l'interface du mode
          guidé pour passer au-dessus ; il ne capte aucun geste, sauf son bouton. */}
      <IndicateurRotation mode={choix.mode} />

      {/* Le témoin de provenance, et le va-et-vient entre local et publié.
          En développement ET sur demande (`?debug=true`) : un visiteur n'a que
          faire de savoir d'où viennent les octets, et une visite livrée ne sert
          jamais que R2. */}
      {import.meta.env.DEV && DEBUG_BADGE && (
        <SourceBadge tour={tour} assetBaseUrl={endpoints.assetBaseUrl} live={LIVE_MODE} />
      )}

      {/* Relevé de VRAM des tuiles — outil de chantier. Il sert à arrêter le
          plafond sur ce que la visite consomme RÉELLEMENT en s'y promenant, et
          non sur un calcul de coin de table. Les deux lignes qui comptent sont
          la crête et les évictions. */}
      {debug && <TileMemoryReadout style={{ position: "absolute", top: 56, right: 12 }} />}

      {/* Les incohérences de références intéressent l'auteur, pas le visiteur — et elles ne
          vont pas sur l'image, qu'elles recouvrent : elles vont à la console. */}
      {import.meta.env.DEV && <IncoherencesEnConsole issues={issues} />}
    </TourPlayer>
  );
}

/**
 * Signale à la console les incohérences de références du document (une piste de voix off
 * absente dans une langue, une scène inconnue…). Ne rend rien : un message posé sur le
 * panorama gêne la visite et, quand il ne bloque rien (la traduction anglaise viendra
 * après le français), ne se lit que comme du bruit. Une seule fois par jeu de messages.
 */
function IncoherencesEnConsole({ issues }: { issues: string[] }) {
  const empreinte = issues.join("\n");
  useEffect(() => {
    if (empreinte) console.warn(`[document] ${issues.length} incohérence(s) de références :\n${empreinte}`);
    // `empreinte` suffit : la liste est reconstruite à chaque rendu, son contenu non.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empreinte]);
  return null;
}

/**
 * Les commandes de la visite libre : le menu, le plan, les variantes.
 *
 * Montrées en visite libre, et aussi quand le visiteur QUITTE un parcours pour
 * explorer (« Explorer librement la visite ») : sans cela, il se retrouverait dans
 * une visite libre sans aucun moyen d'aller d'une scène à l'autre. Jamais sous
 * l'accueil ni pendant un parcours, où elles se disputeraient l'écran avec le fil
 * d'Ariane, les sous-titres et les contrôles.
 */
/**
 * Le coin haut (accueil, langues) cède la place à la bande du guide pendant tout le
 * parcours ; il revient à la sortie, avec la visite libre.
 */
function HorsParcours({ children }: { children: ReactNode }) {
  const enParcours = useTourState((s) => s.guided.state !== "idle");
  return enParcours ? null : <>{children}</>;
}

function Libre({ mode, children }: { mode: "accueil" | "libre" | "parcours" | "demo"; children: ReactNode }) {
  const quitte = useTourState((s) => s.guided.exited);
  const visible = mode === "libre" || ((mode === "parcours" || mode === "demo") && quitte);
  return visible ? <>{children}</> : null;
}

/**
 * `#debug` dans l'adresse — le relevé de mémoire des tuiles ne s'affiche qu'à cette
 * condition. C'est un outil de chantier : il recouvre l'image, et ne regarde que celui qui
 * le demande. Le fragment, et non un paramètre : il ne recharge pas la page, et se
 * suit en direct (on l'ajoute ou on le retire sans perdre sa place dans la visite).
 * Valable aussi en ligne, où l'on peut avoir à mesurer ce que consomme la visite publiée.
 */
function useDebug(): boolean {
  const [debug, setDebug] = useState(() => window.location.hash === "#debug");
  useEffect(() => {
    const onChange = () => setDebug(window.location.hash === "#debug");
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  return debug;
}
